/**
 * Challenge FAQ accordion — open/close height animation.
 *
 * Progressive enhancement over a native <details>/<summary> accordion: without
 * this script the accordion still opens, closes and is keyboard accessible, it
 * just snaps instead of sliding. The height transition itself is only armed
 * once we've taken over (via the .is-animatable class), so a failed script load
 * can never leave a panel stuck at height 0.
 */
(function () {
  'use strict';

  var REDUCED_MOTION = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;

  function prefersReducedMotion() {
    return !!(REDUCED_MOTION && REDUCED_MOTION.matches);
  }

  function initItem(details) {
    if (details.dataset.faqReady === 'true') return;

    var summary = details.querySelector('.challenge-faq__question');
    var panel = details.querySelector('.challenge-faq__answer');

    // Questions without an answer body have nothing to animate.
    if (!summary || !panel) return;

    details.dataset.faqReady = 'true';
    panel.classList.add('is-animatable');
    panel.style.height = details.open ? 'auto' : '0px';

    var animating = false;
    var settleTimer = null;

    function settle() {
      clearTimeout(settleTimer);
      settleTimer = null;
      animating = false;
      if (details.open) {
        // Back to auto so the panel reflows if the text wraps differently
        // after a resize or a font swap.
        panel.style.height = 'auto';
      } else {
        panel.style.height = '0px';
      }
    }

    function finishClose() {
      details.open = false;
      settle();
    }

    summary.addEventListener('click', function (event) {
      event.preventDefault();
      if (animating) return;

      // Motion is not wanted: toggle instantly. We still have to maintain the
      // inline height ourselves, because the panel carries height:0 from init
      // and a 0s transition never fires transitionend to clear it.
      if (prefersReducedMotion()) {
        details.open = !details.open;
        panel.style.height = details.open ? 'auto' : '0px';
        return;
      }

      animating = true;

      if (!details.open) {
        details.open = true;
        var target = panel.scrollHeight;
        panel.style.height = '0px';
        // Two frames: the first commits height:0, the second starts the run.
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            panel.style.height = target + 'px';
          });
        });
        // Safety net in case transitionend never lands (interrupted paint,
        // display:none ancestor, transition removed by another stylesheet).
        settleTimer = setTimeout(settle, 600);
      } else {
        panel.style.height = panel.scrollHeight + 'px';
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            panel.style.height = '0px';
          });
        });
        settleTimer = setTimeout(finishClose, 600);
      }
    });

    panel.addEventListener('transitionend', function (event) {
      if (event.propertyName !== 'height' || event.target !== panel) return;
      if (details.open && panel.style.height === '0px') {
        finishClose();
      } else {
        settle();
      }
    });

    // Keep the inline height honest if the motion preference flips at runtime.
    if (REDUCED_MOTION && REDUCED_MOTION.addEventListener) {
      REDUCED_MOTION.addEventListener('change', function () {
        panel.style.height = details.open ? 'auto' : '0px';
      });
    }
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var items = scope.querySelectorAll('.challenge-faq__item');
    for (var i = 0; i < items.length; i++) initItem(items[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Theme editor re-renders a section's markup in place.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
